// src/pages/team/TeamMemberDetailPage.tsx
// A team member, seen properly: who the person is (userprofile), what their
// membership is (status, joined, invited by), and what they can do (roles).
// Page quick actions (set roles / remove) live in the right drawer.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n, useAuth } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { userprofileApi } from '@core/api';
import type { ListTeamMembersResponse, TeamUserWithMembership } from '@core/types/team';
import type { Userprofile } from '@core/types/userprofile';
import {
  membershipStatusMap,
  getStatusMapping,
  formatDateTime,
  getEntityId,
  getDisplayName,
  refEntity,
} from '@/features/team/logic';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';
import { useOpenDirectConversation } from '@/features/chat/useOpenDirectConversation';
import { usePageActions } from '@/hooks/usePageActions';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Select } from '@/components/Select';
import { EntityAvatar } from '@/components/EntityAvatar';

const ACTION_ICONS = {
  roles: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
  profile: 'M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z',
  remove: 'M22 10.5h-6m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z',
} as const;

// ---------------------------------------------------------------------------
// Small display helpers
// ---------------------------------------------------------------------------

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-2.5 flex items-start justify-between gap-4">
      <dt className="text-sm text-muted-foreground flex-shrink-0">{label}</dt>
      <dd className="text-sm text-foreground text-right min-w-0 break-words">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const TeamMemberDetailPage = observer(() => {
  const { id, memberId } = useParams<{ id: string; memberId: string }>();
  const navigate = useNavigate();
  const store = useTeam();
  const { t, locale } = useI18n();
  const auth = useAuth();
  const dm = useOpenDirectConversation();

  const [member, setMember] = useState<TeamUserWithMembership | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [profile, setProfile] = useState<Userprofile | null>(null);
  const [roles, setRoles] = useState<any[]>([]);
  const [assignRoleId, setAssignRoleId] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [swappingRoleId, setSwappingRoleId] = useState<string | null>(null);
  const [swapTargetId, setSwapTargetId] = useState('');
  const [swapping, setSwapping] = useState(false);
  const [showBulkRoles, setShowBulkRoles] = useState(false);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const findInResponse = (res: ListTeamMembersResponse): TeamUserWithMembership | null =>
    (res.users?.users ?? []).find(
      (m) => m.membership?.entity?.id === memberId || m.user?.entity?.id === memberId,
    ) ?? null;

  useEffect(() => {
    if (!id || !memberId) return;
    store.GetTeam(id);

    store.ListTeamMembers(id).then((res) => {
      const found = findInResponse(res);
      if (found) setMember(found);
      else setNotFound(true);
    }).catch(() => setNotFound(true));

    store.ListTeamRoles(id).then((res) => {
      setRoles(res.roles?.roles ?? []);
    }).catch(() => {});

    return () => { store.clearCurrent(); };
  }, [id, memberId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The membership references a profile — that is where the person's real
  // info lives (name, bio, location, timezone), not on the thin Actor.
  const profileId = member?.membership?.profile?.id;
  useEffect(() => {
    if (!profileId) return;
    let alive = true;
    userprofileApi.GetUserprofile(profileId)
      .then((res) => { if (alive) setProfile(res.userprofile ?? null); })
      .catch(() => { /* profile is enrichment, not required */ });
    return () => { alive = false; };
  }, [profileId]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const reloadMember = async () => {
    if (!id) return;
    const res = await store.ListTeamMembers(id);
    const found = findInResponse(res);
    if (found) setMember(found);
  };

  const userEntityId = member?.user?.entity?.id;

  const handleAssignRole = async () => {
    if (!id || !userEntityId || !assignRoleId) return;
    try {
      await store.AssignTeamRole(id, {
        team: refEntity(id),
        user: refEntity(userEntityId),
        role: refEntity(assignRoleId),
        message: '',
      });
      setAssignRoleId('');
      showSuccess(t('team.roleAssigned'));
      await reloadMember();
    } catch { /* store handles */ }
  };

  const handleRevokeRole = async (roleId: string) => {
    if (!id || !userEntityId) return;
    try {
      await store.RevokeTeamRole(id, {
        team: refEntity(id),
        user: refEntity(userEntityId),
        role: refEntity(roleId),
        message: '',
      });
      showSuccess(t('team.roleRevoked'));
      await reloadMember();
    } catch { /* store handles */ }
  };

  const handleSwapRole = async (oldRoleId: string) => {
    if (!id || !memberId || !swapTargetId) return;
    const userId = userEntityId || memberId;
    setSwapping(true);
    try {
      await store.UpdateTeamUserRole(id, userId, {
        team: { id },
        user: { id: userId },
        old_role: { id: oldRoleId },
        new_role: { id: swapTargetId },
      } as never);
      setSwappingRoleId(null);
      setSwapTargetId('');
      showSuccess(t('team.roleChanged'));
      await reloadMember();
    } catch { /* store handles */ } finally { setSwapping(false); }
  };

  const toggleBulkRole = (roleId: string) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId); else next.add(roleId);
      return next;
    });
  };

  const openBulkRoles = () => {
    const currentRoles = member?.roles?.roles ?? [];
    setBulkSelected(new Set(currentRoles.map((r) => getEntityId(r))));
    setShowBulkRoles(true);
  };

  const handleBulkSetRoles = async () => {
    if (!id || !memberId) return;
    setBulkSaving(true);
    try {
      const selectedRoles = roles.filter((r: any) => bulkSelected.has(getEntityId(r)));
      await store.UpdateTeamMemberRole(id, memberId, {
        membership: { id: memberId },
        roles: { roles: selectedRoles },
        message: '',
        status: member?.membership?.status || '',
      } as never);
      setShowBulkRoles(false);
      showSuccess(t('team.rolesUpdated'));
      await reloadMember();
    } catch { /* store handles */ } finally { setBulkSaving(false); }
  };

  const handleRemove = async () => {
    if (!id || !memberId) return;
    setRemoving(true);
    try {
      await store.RemoveTeamMember(id, memberId);
      navigate(`/teams/${id}/members`);
    } catch { /* store handles */ } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  };

  const isOwner = member?.is_owner ?? false;

  // Page quick actions → right drawer.
  usePageActions([
    ...(profileId ? [{
      key: 'open-profile',
      icon: ACTION_ICONS.profile,
      label: t('team.member.openProfile'),
      onSelect: () => navigate(`/userprofiles/${profileId}`),
    }] : []),
    ...(!isOwner && roles.length > 0 ? [{
      key: 'set-roles',
      icon: ACTION_ICONS.roles,
      label: t('team.setRoles'),
      onSelect: openBulkRoles,
    }] : []),
    ...(!isOwner ? [{
      key: 'remove',
      icon: ACTION_ICONS.remove,
      label: t('common.remove'),
      onSelect: () => setConfirmRemove(true),
    }] : []),
  ]);

  const teamName = (store.currentItem as any)?.name || t('team.title');

  // ── Loading / not found ──────────────────────────────────────────
  if (!member) {
    return (
      <div className="space-y-6">
        {notFound ? (
          <Alert variant="warning" title={t('common.notFound')}>
            <Link to={`/teams/${id}/members`} className="text-sm underline text-primary">
              {t('team.members')}
            </Link>
          </Alert>
        ) : (
          <div className="space-y-6" aria-hidden="true">
            <div className="h-4 w-64 rounded bg-muted animate-pulse" />
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-muted animate-pulse" />
              <div className="space-y-2">
                <div className="h-6 w-48 rounded bg-muted animate-pulse" />
                <div className="h-4 w-32 rounded bg-muted animate-pulse" />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 h-64 rounded-xl bg-muted animate-pulse" />
              <div className="h-64 rounded-xl bg-muted animate-pulse" />
            </div>
          </div>
        )}
      </div>
    );
  }

  const displayName =
    profile?.display_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    member.user?.display_name ||
    '--';
  const username = profile?.username;
  const avatarUrl = member.user?.avatar || '';
  const status = member.membership?.status || 'MEMBERSHIP_STATUS_ACTIVE';
  const statusMapping = getStatusMapping(membershipStatusMap, status);
  const memberRoles = member.roles?.roles ?? [];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm" aria-label="Breadcrumb">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}/members`} className="text-muted-foreground hover:text-foreground">{t('team.members')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{displayName}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="members" />

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      {/* Header */}
      <div className="flex items-center gap-4">
        <EntityAvatar name={displayName} image={avatarUrl} resource={profile?.avatar} profileId={profileId} className="h-16 w-16 text-xl" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground truncate">{displayName}</h1>
            {member.is_owner && <Badge variant="warning">{t('team.field.owner')}</Badge>}
            {member.is_admin && !member.is_owner && <Badge variant="primary">{t('common.admin')}</Badge>}
            <Badge variant={statusMapping.variant as never}>{t(statusMapping.labelKey)}</Badge>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-sm text-muted-foreground">
            {username && <span>@{username}</span>}
            {profileId && (
              <Link to={`/userprofiles/${profileId}`} className="text-primary hover:underline">
                {t('team.member.openProfile')}
              </Link>
            )}
          </div>
        </div>
        {profileId && profileId !== auth.activeProfileId && (
          <div className="ml-auto flex flex-col items-end gap-1 self-start">
            <Button
              size="sm"
              variant="secondary"
              disabled={dm.isOpening}
              onClick={() => dm.open(profileId)}
              className="transition-transform active:scale-[0.96]"
            >
              {t('chat.people.message')}
            </Button>
            {dm.error ? (
              <p className="max-w-[220px] text-right text-xs text-destructive">
                {t('chat.people.openFailed')}
              </p>
            ) : null}
          </div>
        )}
      </div>

      {profile?.bio && (
        <p className="text-sm text-muted-foreground max-w-2xl">{profile.bio}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Roles — the job this page is for */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t('team.memberRolesCount', { count: memberRoles.length })}</CardTitle>
            {!member.is_owner && roles.length > 0 && (
              <Button size="sm" variant="secondary" onClick={() => (showBulkRoles ? setShowBulkRoles(false) : openBulkRoles())}>
                {showBulkRoles ? t('common.cancel') : t('team.setRoles')}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {showBulkRoles ? (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">{t('team.setRolesDescription')}</p>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {roles.map((r: any) => {
                    const rId = getEntityId(r);
                    return (
                      <label key={rId} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-muted/50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bulkSelected.has(rId)}
                          onChange={() => toggleBulkRole(rId)}
                          className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                        />
                        <span className="text-sm text-foreground">{r.name}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                  <Button variant="ghost" onClick={() => setShowBulkRoles(false)}>{t('common.cancel')}</Button>
                  <Button onClick={handleBulkSetRoles} disabled={bulkSaving}>
                    {bulkSaving ? t('common.saving') : t('common.saveChanges')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {memberRoles.length > 0 ? (
                  <div className="space-y-2">
                    {memberRoles.map((role: any) => {
                      const roleId = getEntityId(role);
                      const otherRoles = roles.filter((r: any) => getEntityId(r) !== roleId);
                      return (
                        <div key={roleId} className="p-3 rounded-lg border border-border">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                              {role.color && <div className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: role.color }} aria-hidden="true" />}
                              <Link to={`/teams/${id}/roles/${roleId}`} className="text-sm font-medium text-primary hover:underline truncate">
                                {role.name}
                              </Link>
                              {role.is_default && <Badge variant="primary" size="sm">{t('team.defaultRole')}</Badge>}
                              {role.is_system && <Badge variant="secondary" size="sm">{t('team.systemRole')}</Badge>}
                            </div>
                            {!role.is_system && !member.is_owner && (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button size="sm" variant="ghost" onClick={() => { setSwappingRoleId(swappingRoleId === roleId ? null : roleId); setSwapTargetId(''); }}>
                                  {t('team.changeRole')}
                                </Button>
                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleRevokeRole(roleId)}>
                                  {t('team.revokeRole')}
                                </Button>
                              </div>
                            )}
                          </div>
                          {swappingRoleId === roleId && (
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
                              <Select value={swapTargetId} onChange={(e) => setSwapTargetId(e.target.value)} className="flex-1">
                                <option value="">{t('team.changeRoleTo')}</option>
                                {otherRoles.map((r: any) => (
                                  <option key={getEntityId(r)} value={getEntityId(r)}>{r.name}</option>
                                ))}
                              </Select>
                              <Button size="sm" onClick={() => handleSwapRole(roleId)} disabled={!swapTargetId || swapping}>
                                {swapping ? t('common.saving') : t('common.confirm')}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('team.noRolesAssigned')}</p>
                )}

                {!member.is_owner && roles.length > 0 && (
                  <div className="flex gap-4 pt-2 border-t border-border">
                    <Select value={assignRoleId} onChange={(e) => setAssignRoleId(e.target.value)} className="flex-1">
                      <option value="">{t('team.selectRoleToAssign')}</option>
                      {roles.filter((r: any) => !memberRoles.some((mr: any) => getEntityId(mr) === getEntityId(r))).map((r: any) => (
                        <option key={getEntityId(r)} value={getEntityId(r)}>{r.name}</option>
                      ))}
                    </Select>
                    <Button onClick={handleAssignRole} disabled={!assignRoleId}>{t('team.assignRole')}</Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* About + membership */}
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>{t('team.member.about')}</CardTitle></CardHeader>
            <CardContent>
              {profile ? (
                <dl className="divide-y divide-border/60">
                  {username && <InfoRow label={t('auth.username')}>@{username}</InfoRow>}
                  {profile.location && <InfoRow label={t('userprofile.location')}>{profile.location}</InfoRow>}
                  {profile.timezone && <InfoRow label={t('userprofile.timezone')}>{profile.timezone}</InfoRow>}
                  {profile.language && <InfoRow label={t('userprofile.language')}>{profile.language}</InfoRow>}
                  {profile.created_at && (
                    <InfoRow label={t('team.member.profileSince')}>{formatDateTime(profile.created_at, locale)}</InfoRow>
                  )}
                  {!username && !profile.location && !profile.timezone && !profile.language && (
                    <p className="py-2 text-sm text-muted-foreground">{t('team.member.emptyProfile')}</p>
                  )}
                </dl>
              ) : (
                <p className="py-2 text-sm text-muted-foreground">{t('team.member.noProfile')}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>{t('team.member.membership')}</CardTitle></CardHeader>
            <CardContent>
              <dl className="divide-y divide-border/60">
                <InfoRow label={t('common.status')}>
                  <Badge variant={statusMapping.variant as never} size="sm">{t(statusMapping.labelKey)}</Badge>
                </InfoRow>
                <InfoRow label={t('team.memberJoined')}>{formatDateTime(member.membership?.joined_at, locale)}</InfoRow>
                {member.membership?.invited_by && (
                  <InfoRow label={t('team.memberInvitedBy')}>{getDisplayName(member.membership.invited_by)}</InfoRow>
                )}
                {member.membership?.notes && (
                  <InfoRow label={t('team.member.notes')}>{member.membership.notes}</InfoRow>
                )}
              </dl>
            </CardContent>
          </Card>

          {!member.is_owner && (
            <Button variant="danger" className="w-full" onClick={() => setConfirmRemove(true)}>
              {t('common.remove')}
            </Button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title={t('common.remove')}
        description={t('team.removeMemberConfirm')}
        destructive
        isConfirming={removing}
        onConfirm={handleRemove}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
});

export default TeamMemberDetailPage;
